import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import axios from "axios";
import classnames from "classnames";
import { useInterval } from "usehooks-ts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRoad } from '@fortawesome/free-solid-svg-icons/faRoad';
import { AdvancedMarker, GoogleMap } from '../../components/googleMap';
import { axiosErrorHandler } from "../../../../swzutils";
import DriverLocationInfoWindow from '../map/DriverLocationInfoWindow';
import WaypointInfoWindow from '../map/WaypointInfoWindow';
import MarkerSmallBuilder from "../map/markers/MarkerSmallBuilder";
import MarkerLargeBuilder from "../map/markers/MarkerLargeBuilder";
import PhotoInfoWindow, {} from "../map/PhotoInfoWindow";
import DriverLocationSlider from "../map/DriverLocationSlider";
import ConsignmentImageKind from "../../enums/ConsignmentImageKind";
import { userHasPermission } from "../../../../swzutils/UserPermissionsUtil";
import { ConsignmentStatusEnum } from "./api/Representation";
import API from "../../../../API";
import CachedTenantSettingsRepository from "../../../../CachedTenantSettingsRepository";
import { loggedInUserSelector } from "../../../../store/slice/auth";
import { useAppDispatch, useAppSelector } from "../../../../store";
const ConsignmentMap = ({ consignment }) => {
    const dispatch = useAppDispatch();
    const loggedInUser = useAppSelector(loggedInUserSelector);
    const [showTraffic, setShowTraffic] = useState(false);
    const [autoRefreshOn, setAutoRefreshOn] = useState(false);
    const [driverLocation, setDriverLocation] = useState(null);
    const [driverLocationInfoWindowVisible, setDriverLocationInfoWindowVisible] = useState(false);
    const [pickupAddressInfoWindowVisible, setPickupAddressInfoWindowVisible] = useState(false);
    const [deliveryAddressInfoWindowVisible, setDeliveryAddressInfoWindowVisible] = useState(false);
    const pickupMarkerRef = useRef(null);
    const deliveryMarkerRef = useRef(null);
    const [addressMapMarkers, setAddressMapMarkers] = useState([]);
    const [selectedImage, setSelectedImage] = useState(null);
    const [imageMapMarkers, setImageMapMarkers] = useState([]);
    const [locationHistory, setLocationHistory] = useState([]);
    const [tenantSettings, setTenantSettings] = useState(null);
    useEffect(() => {
        if (consignment?.id) {
            onConsignmentChanged(consignment.id);
            const tenantId = consignment?.tenantId;
            if (tenantId) {
                CachedTenantSettingsRepository.getSettingsForTenant(tenantId, dispatch)
                    .then(setTenantSettings);
            }
        }
    }, [consignment?.id]);
    useInterval(() => {
        if (consignment?.id) {
            loadLocationHistory(consignment.id);
            loadDriverLocation(consignment.id);
        }
    }, autoRefreshOn ? 30000 : null);
    const loadLocationHistory = (consignmentId) => {
        if (!userHasPermission(loggedInUser, ['admin:consignmentLocation:viewHistory'])) {
            return;
        }
        axios.get(`/admin/v1/consignments/${consignmentId}/location-history`)
            .then(response => setLocationHistory(response.data))
            .catch((error) => {
            setAutoRefreshOn(false);
            axiosErrorHandler(error);
        });
    };
    const loadDriverLocation = (consignmentId) => {
        if (!userHasPermission(loggedInUser, ['admin:consignmentLocation:view'])) {
            return;
        }
        axios.get(`/admin/v1/consignments/${consignmentId}/driver-location`)
            .then(response => {
            if (response.status === 204) { // No content
                setDriverLocation(null);
                return;
            }
            setDriverLocation({
                ...response.data,
                markerRef: React.createRef()
            });
        })
            .catch((error) => {
            setAutoRefreshOn(false);
            if (error.response.status !== 404) {
                axiosErrorHandler(error);
            }
        });
    };
    const onConsignmentChanged = (consignmentId) => {
        loadLocationHistory(consignmentId);
        loadConsignmentImages(consignmentId);
        buildAddressMapMarkers();
        if (consignment.status === ConsignmentStatusEnum.STARTED) {
            loadDriverLocation(consignmentId);
            setAutoRefreshOn(true);
        }
        else {
            setAutoRefreshOn(false);
        }
    };
    const loadConsignmentImages = (consignmentId) => {
        axios.get(`/admin/v1/consignments/${consignmentId}/images`)
            .then(response => {
            const consignmentImages = response.data.results.map(item => {
                return {
                    id: item.id,
                    kind: item.kind,
                    title: ConsignmentImageKind.enumDescription(item.kind),
                    comments: item.comments,
                    url: `${API.origin()}/admin/v1/consignments/${consignmentId}/images/${item.id}/content`,
                    thumbUrl: `${API.origin()}/admin/v1/consignments/${consignmentId}/images/${item.id}/thumbnail`,
                    createdAt: item.createdAt,
                    latitude: item.latitude,
                    longitude: item.longitude,
                    markerRef: React.createRef()
                };
            });
            buildImageMapMarkers(consignmentImages);
        })
            .catch((error) => {
            setAutoRefreshOn(false);
            if (error.response.status !== 404) {
                axiosErrorHandler(error);
            }
        });
    };
    const buildImageMapMarkers = (consignmentImages) => {
        const imageMarkers = consignmentImages.filter(image => image.latitude && image.longitude)
            .map(image => {
            return (_jsx(AdvancedMarker, { ref: image.markerRef, title: "Docket", imageUri: "/assets/images/map-marker-image-round.svg", position: {
                    lat: image.latitude,
                    lng: image.longitude
                }, onClick: () => setSelectedImage(image) }, `img-${image.id}`));
        });
        setImageMapMarkers(imageMarkers);
    };
    const buildAddressMapMarkers = () => {
        const addressMapMarkers = [];
        const pickupAddress = consignment.pickupAddress;
        if (pickupAddress && pickupAddress.latitude && pickupAddress.longitude) {
            addressMapMarkers.push(_jsx(AdvancedMarker, { ref: pickupMarkerRef, onClick: () => setPickupAddressInfoWindowVisible(true), imageUri: "/assets/images/map-marker-pickup-round.svg", position: {
                    lat: pickupAddress.latitude,
                    lng: pickupAddress.longitude
                }, zIndex: 9 }, "pickup"));
        }
        const deliveryAddress = consignment.deliveryAddress;
        if (deliveryAddress && deliveryAddress.latitude && deliveryAddress.longitude) {
            addressMapMarkers.push(_jsx(AdvancedMarker, { ref: deliveryMarkerRef, onClick: () => setDeliveryAddressInfoWindowVisible(true), imageUri: "/assets/images/map-marker-drop-off-round.svg", position: {
                    lat: deliveryAddress.latitude,
                    lng: deliveryAddress.longitude
                }, zIndex: 9 }, "delivery"));
        }
        setAddressMapMarkers(addressMapMarkers);
    };
    const onMapClicked = () => {
        setDriverLocationInfoWindowVisible(false);
        setPickupAddressInfoWindowVisible(false);
        setDeliveryAddressInfoWindowVisible(false);
    };
    return (_jsx("div", { style: { height: 600 }, children: _jsxs("div", { className: "map-body", children: [_jsx("div", { id: "map-option-buttons", children: _jsx("button", { className: classnames('btn', {
                            "on": showTraffic,
                            "off": !showTraffic
                        }), onClick: () => setShowTraffic(!showTraffic), children: _jsx(FontAwesomeIcon, { icon: faRoad }) }) }), _jsxs(GoogleMap, { centerAroundCurrentLocation: false, boundMapToMarkers: true, showTraffic: showTraffic, onClick: onMapClicked, children: [imageMapMarkers, addressMapMarkers, driverLocation?.id &&
                            _jsx(AdvancedMarker, { onClick: () => setDriverLocationInfoWindowVisible(true), imageUri: driverLocation.vehicle && driverLocation.vehicle.registration
                                    ? MarkerLargeBuilder.buildMarker(driverLocation, loggedInUser, consignment?.tenantId)
                                    : MarkerSmallBuilder.buildMarker(driverLocation), data: driverLocation, title: driverLocation.vehicle && driverLocation.vehicle.type && driverLocation.vehicle.type.description, ref: driverLocation.markerRef, position: {
                                    lat: driverLocation.latitude,
                                    lng: driverLocation.longitude
                                }, zIndex: 10 }, driverLocation.id), _jsx(DriverLocationInfoWindow, { driverLocation: driverLocation, showConsignmentSummary: false, showVehicleHireSummary: false, markerRef: driverLocation?.markerRef, visible: driverLocationInfoWindowVisible, onClose: () => setDriverLocationInfoWindowVisible(false) }), pickupMarkerRef.current &&
                            _jsx(WaypointInfoWindow, { windowKey: "PickupAddressInfoWindow", markerRef: pickupMarkerRef, companyName: consignment?.pickupCompanyName, contactName: consignment?.pickupContactName, contactTelephone: consignment?.pickupTelephone, address: consignment?.pickupAddress, visible: pickupAddressInfoWindowVisible, onClose: () => setPickupAddressInfoWindowVisible(false) }), deliveryMarkerRef.current &&
                            _jsx(WaypointInfoWindow, { windowKey: "DeliveryAddressInfoWindow", markerRef: deliveryMarkerRef, companyName: consignment.deliveryCompanyName, contactName: consignment.deliveryContactName, contactTelephone: consignment.deliveryTelephone, address: consignment.deliveryAddress, visible: deliveryAddressInfoWindowVisible, onClose: () => setDeliveryAddressInfoWindowVisible(false) }), _jsx(PhotoInfoWindow, { windowKey: "PhotoInfoWindow", visible: !!selectedImage, imageMarker: selectedImage, onClose: () => setSelectedImage(null) }), tenantSettings && locationHistory &&
                            _jsx(DriverLocationSlider, { driverLocationHistory: locationHistory, driverIdleAlertMinutes: tenantSettings.driverIdleAlertMinutes })] }, consignment.id)] }) }));
};
export default ConsignmentMap;
